import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Check, Play, Square, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

const NOT_MECHANIC = /^(admin|reception|atelier)-default/;
export const isMechanic = (e) => e.is_active !== false && !['atelier', 'reception'].includes(e.role) && !NOT_MECHANIC.test(e.id);
export const empName = (e) => `${e.first_name || ''} ${e.last_name || ''}`.trim();

export const assignedIds = (wo) => wo.assigned_employee_ids?.length ? wo.assigned_employee_ids : (wo.assigned_employee_id ? [wo.assigned_employee_id] : []);

export const MechanicsPicker = ({ woId, wo, employees, onChanged }) => {
  const [ids, setIds] = useState(() => assignedIds(wo));
  const toggle = async (id) => {
    let next;
    setIds(cur => { next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]; return next; });
    try { await axios.put(`${API}/work-orders/${woId}`, { assigned_employee_ids: next }); onChanged(); }
    catch { toast.error('Erreur sauvegarde'); }
  };
  return (
    <Card className={`p-4 border-2 ${ids.length ? 'border-slate-900' : 'border-red-400 bg-red-50'}`} data-testid="mechanics-picker">
      <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5" /><h2 className="text-base font-black uppercase tracking-wide">Mécaniciens sur ce véhicule</h2></div>
      <p className={`text-xs mb-3 flex items-center gap-1 ${ids.length ? 'text-slate-500' : 'text-red-700 font-semibold'}`} data-testid="mechanics-hint">
        {!ids.length && <AlertTriangle className="w-3.5 h-3.5" />}
        {ids.length ? 'Touchez un nom pour ajouter ou retirer un mécanicien (plusieurs possibles).' : 'Choisissez au moins un mécanicien : impossible de démarrer le chrono sans nom.'}
      </p>
      <div className="flex flex-wrap gap-2">
        {employees.map(e => {
          const on = ids.includes(e.id);
          return (
            <button key={e.id} type="button" onClick={() => toggle(e.id)} data-testid={`mechanic-chip-${e.id}`}
              className={`px-4 py-2.5 rounded-full text-sm font-bold border-2 flex items-center gap-1.5 transition-colors touch-manipulation ${on ? 'bg-[#0E1116] text-[#FACC15] border-[#0E1116]' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-900'}`}>
              {on && <Check className="w-4 h-4" />}{empName(e)}
            </button>
          );
        })}
        {employees.length === 0 && <p className="text-sm text-slate-500">Aucun employé actif — ajoutez-les dans RH & Employés.</p>}
      </div>
    </Card>
  );
};

const fmt = (s) => `${Math.floor(s / 3600) ? `${Math.floor(s / 3600)}h ` : ''}${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const TimerRow = ({ woId, name, entries, onChanged }) => {
  const active = entries.find(e => !e.stopped_at && e.mechanic_name === name);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!active) return undefined; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [active]);
  const total = entries.filter(e => e.mechanic_name === name).reduce((s, e) => s + (e.duration_minutes || 0), 0);
  const act = async (kind) => {
    try { const r = await axios.post(`${API}/atelier/work-orders/${woId}/time/${kind}`, { mechanic_name: name }); toast.success(kind === 'start' ? `Chrono démarré — ${name}` : r.data.message); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const slug = name.replace(/\s+/g, '-').toLowerCase();
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border-2 ${active ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`} data-testid={`timer-row-${slug}`}>
      <div className="flex-1 min-w-0"><p className="font-bold truncate">{name}</p>
        <p className="text-xs text-slate-500">Total sur la fiche : {Math.floor(total / 60)}h{String(Math.round(total % 60)).padStart(2, '0')}</p></div>
      {active && <span className="font-mono text-2xl font-bold text-blue-600" data-testid={`timer-display-${slug}`}>{fmt(Math.max(0, Math.floor((now - new Date(active.started_at).getTime()) / 1000)))}</span>}
      {active
        ? <Button onClick={() => act('stop')} className="bg-red-600 hover:bg-red-700 h-12 px-5" data-testid={`stop-timer-${slug}`}><Square className="w-4 h-4 mr-1 fill-white" />Arrêter</Button>
        : <Button onClick={() => act('start')} className="bg-blue-600 hover:bg-blue-700 h-12 px-5" data-testid={`start-timer-${slug}`}><Play className="w-4 h-4 mr-1 fill-white" />Démarrer</Button>}
    </div>
  );
};

export const MechanicTimers = ({ woId, names, entries, onChanged }) => (
  <Card className="p-4 space-y-2" data-testid="time-tracking-section">
    <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Temps de travail</h3>
    {names.length === 0
      ? <p className="text-sm font-semibold text-red-700 p-3 rounded-lg bg-red-50" data-testid="timer-blocked">Choisissez le mécanicien en haut de la fiche pour démarrer le chrono.</p>
      : names.map(n => <TimerRow key={n} woId={woId} name={n} entries={entries} onChanged={onChanged} />)}
  </Card>
);
